import React, { memo, useEffect } from 'react';
import { withRouter, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { usePageSchema } from 'hooks/usePageSchema';
import { fetchMbComments } from 'actions';
import { shouldNavigateToCurrencyYearArchive } from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import MbPageContent from 'components/MbPageContent';
import PageSidebar from 'components/PageSidebar';
import LayoutContainer from 'components/LayoutContainer';
import MbPageFooter from 'components/MbPageFooter';

import { Content, Wrapper } from './styles';

const filterByWhiteList = (data, activeKey) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const key = activeKey ? activeKey.toUpperCase() : 'USD';
  const currencyItems = data[key];
  const item = Array.isArray(currencyItems) ? currencyItems[0] : null;

  if (!item) {
    return [];
  }

  return [{ ...item }];
};

const MbPage = memo(({ location, staticContext }) => {
  const intl = useIntl();
  const params = useParams();
  const dispatch = useDispatch();

  const mbCommentsData = useSelector(
    state => state?.currency?.mbComments?.data
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, params.currency]);

  useEffect(() => {
    if (!mbCommentsData) {
      dispatch(fetchMbComments({ intl }));
    }
  }, [mbCommentsData, intl, dispatch]);

  const data = filterByWhiteList(mbCommentsData, params.currency);

  const mainEntityName = intl.formatMessage({
    id: `mbRates`,
  });

  const defaultMetaTitle = intl.formatMessage({
    id: `homePage.metaTitle`,
  });

  const defaultMetaDescription = intl.formatMessage({
    id: `homePage.metaDescription`,
  });

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    mainEntity: data,
    mainEntityName,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  if (shouldNavigateToCurrencyYearArchive(params.date)) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <MbPageContent />
            <PageSidebar isInfoBlockDisplayed isCompaniesNewsDisplayed />
          </PageInner>
          <MbPageFooter />
        </LayoutContainer>
      </Content>
    </Page>
  );
});

MbPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

MbPage.defaultProps = {
  staticContext: null,
};

export default withRouter(MbPage);
